import React from 'react';
import { User, Mail, Phone, Download } from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Loader } from '../../components/ui/Loader';
import { useAuth } from '../../contexts/AuthContext';
import { useSchoolRegistration, useActiveEvents } from '../../hooks/useConvex';
import { Id } from '../../../convex/_generated/dataModel';

export const TeamPage: React.FC = () => {
    const { school } = useAuth();
    const registration = useSchoolRegistration(school?._id as Id<'schools'> | undefined);
    const events = useActiveEvents();
    const loading = registration === undefined || events === undefined;

    if (loading) {
        return (
            <div className="flex justify-center items-center h-[60vh]">
                <Loader size="lg" />
            </div>
        );
    }

    return (
        <div className="space-y-8">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className="text-4xl font-heading font-bold text-white mb-2 tracking-tight">
                        My <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF991C] to-[#FFB84D]">Team</span>
                    </h1>
                    <p className="text-gray-400 text-lg">
                        View your team roster and participant details
                    </p>
                </div>
                <div className="flex gap-3">
                    <Button variant="outline" className="hover:bg-white/5">
                        <Download className="w-4 h-4 mr-2" />
                        Download All ID Cards
                    </Button>
                </div>
            </div>

            {/* Team Grid */}
            {!registration?.students?.length ? (
                <Card className="p-12 text-center border-dashed border-white/10 bg-transparent">
                    <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-4">
                        <User className="w-8 h-8 text-gray-500" />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-2">No Team Members Yet</h3>
                    <p className="text-gray-400 mb-6">
                        You haven't registered any participants yet. Go to the Registration page to add students.
                    </p>
                    <Button variant="primary" onClick={() => window.location.href = '/school/register'}>
                        Go to Registration
                    </Button>
                </Card>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {registration.students.map((student, index) => (
                        <Card key={index} className="overflow-hidden border-white/5 bg-black/40 backdrop-blur-xl hover:border-brand/30 transition-all duration-300 group">
                            <div className="p-6">
                                <div className="flex items-start justify-between mb-4">
                                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#FF991C] to-[#FFB84D] p-[1px]">
                                        <div className="w-full h-full rounded-full bg-black flex items-center justify-center overflow-hidden">
                                            {student.photo_url ? (
                                                <img src={student.photo_url} alt={student.name} className="w-full h-full object-cover" />
                                            ) : (
                                                <User className="w-6 h-6 text-white" />
                                            )}
                                        </div>
                                    </div>
                                    <Badge variant="neutral" size="sm" className="bg-white/5">
                                        Grade {student.grade}
                                    </Badge>
                                </div>

                                <h3 className="text-xl font-heading font-bold text-white mb-1 group-hover:text-brand transition-colors">
                                    {student.name}
                                </h3>

                                <div className="space-y-2 mt-4">
                                    {student.email && (
                                        <div className="flex items-center text-sm text-gray-400">
                                            <Mail className="w-4 h-4 mr-2 text-gold-500" />
                                            {student.email}
                                        </div>
                                    )}
                                    {student.contact && (
                                        <div className="flex items-center text-sm text-gray-400">
                                            <Phone className="w-4 h-4 mr-2 text-gold-500" />
                                            {student.contact}
                                        </div>
                                    )}
                                </div>

                                <div className="mt-6 pt-4 border-t border-white/10">
                                    <p className="text-xs text-gray-500 uppercase tracking-wider mb-2 font-bold">Events</p>
                                    <div className="flex flex-wrap gap-2">
                                        {student.events.map(eventId => {
                                            const event = events?.find(e => e._id === eventId);
                                            return event ? (
                                                <Badge key={eventId} variant="neutral" size="sm" className="bg-white/5 border-white/10 text-xs">
                                                    {event.name}
                                                </Badge>
                                            ) : null;
                                        })}
                                        {student.events.length === 0 && (
                                            <span className="text-sm text-gray-600 italic">No events assigned</span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </Card>
                    ))}
                </div>
            )}
        </div>
    );
};
